import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { CarTask } from "../../http/carTasks/types";
import { useAdminCarsApi } from "../../http/cars/api";
import { Car, CarNoCarDepency } from "../../http/cars/types";
import { useDefaultTasksApi } from "../../http/defaultTasks/api";
import { DefaultTask } from "../../http/defaultTasks/types";
import { PaginationQueryWithFields } from "../../types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const DEFAULT_TASKS_FIELDS = ["id", "name", "isSystemTask"] as const;
type DefaultTaskField = (typeof DEFAULT_TASKS_FIELDS)[number];
type DefaultTaskSearch = Pick<DefaultTask, DefaultTaskField>;

export interface CarTaskFormBody {
  name?: string;
  defaultTask?: DefaultTask;
  car?: Car;
}

type CarTaskFormProps = {
  carTask?: CarTask | null;
  onSubmit: (form: CarTaskFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  carId?: string;
  isWithDefaultTask?: boolean;
};

const CarTaskForm: FC<CarTaskFormProps> = ({
  carTask,
  onSubmit,
  isDisabled,
  isLoading,
  carId,
  isWithDefaultTask,
}: CarTaskFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();
  const defaultTasksApi = useDefaultTasksApi();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const defaultValues = {
    name: carTask?.taskName,
    car: carTask?.car,
  };

  const query: PaginationQueryWithFields<DefaultTaskSearch> = {
    limit: 100,
    fields: DEFAULT_TASKS_FIELDS,
  };

  const resultDefTasks = defaultTasksApi.useAdminDefaultTasks(query, aborter.current.signal);
  const [dataDefTasks] = useDataInitializer(resultDefTasks.data, resultDefTasks.error);

  useEffect(() => {
    resultDefTasks.error && dispatch(showErrorNotification(i18n, resultDefTasks.error));
  }, [resultDefTasks.error]);

  return (
    <Form<CarTaskFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ register, reset, formState: { errors }, control }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [carTask]);
        return (
          <FormFieldsContainer>
            {!carId && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          {...field}
                          inputId={labelId}
                          isDisabled={isDisabled || isLoading}
                          isLoading={isLoading}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<{
                            id: string;
                            vehicleIdentificationNumber: string;
                            currentPlateNumber?: string;
                            lastPlateNumber?: string;
                          }>(isInvalid)}
                          menuPosition="fixed"
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}

            {!isWithDefaultTask && (
              <FormField label={i18n.t.common.name} error={errors.name}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading}
                    {...register("name", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>
            )}

            {!!isWithDefaultTask && (
              <Controller
                name="defaultTask"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.defaultTasks.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        {...field}
                        menuPosition="fixed"
                        inputId={labelId}
                        isDisabled={isDisabled || isLoading}
                        isLoading={isLoading}
                        options={dataDefTasks?.items}
                        isClearable
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.name}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<DefaultTaskSearch>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CarTaskForm;
